if (!customElements.get('cart-coupon')) {
  customElements.define('cart-coupon', class CartCoupon extends HTMLElement {

    /* ── lifecycle ──────────────────────────────────────────── */

    connectedCallback() {
      this._code        = this.getAttribute('data-applied-code') || sessionStorage.getItem('applied_coupon_code') || null;
      this._input       = this.querySelector('[data-coupon-input]');
      this._applyBtn    = this.querySelector('[data-coupon-apply]');
      this._removeEl    = this.querySelector('[data-coupon-remove]');
      this._inputWrap   = this.querySelector('[data-coupon-input-wrap]');
      this._appliedWrap = this.querySelector('[data-coupon-applied]');
      this._appliedText = this.querySelector('[data-coupon-applied-text]');
      this._feedback    = this.querySelector('[data-coupon-feedback]');
      this._validateId  = 0;
      this._debounceTimer = null;

      this._applyBtn.addEventListener('click', this._onApply.bind(this));

      const doRemove = this._onRemove.bind(this);
      this._removeEl.addEventListener('click', doRemove);
      this._removeEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); doRemove(e); }
      });

      this._input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); clearTimeout(this._debounceTimer); this._applyBtn.click(); }
      });
      this._input.addEventListener('input', () => {
        this._clearFeedback();
        clearTimeout(this._debounceTimer);
      });

      CartCoupon._attachCheckoutInterceptor();
      CartCoupon._watchCartRerenders();

      if (this._code) {
        this._showApplied(this._code);
        CartCoupon.stampBadges(this._code);
      }
    }

    /* ── public ─────────────────────────────────────────────── */

    get code() { return this._code; }

    /* ── private actions ────────────────────────────────────── */

    _onApply() {
      const code = this._input.value.trim().toUpperCase();
      if (!code) {
        this._setFeedback(this.dataset.emptyMsg, 'error');
        this._input.focus();
        return;
      }
      this._validate(code, true);
    }

    _onRemove(e) {
      if (e) { e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation(); }
      this._code = null;
      sessionStorage.removeItem('applied_coupon_code');
      this._showInput(true);
      CartCoupon.removeBadges();
      this._clearFeedback();
      this.removeAttribute('data-applied-code');
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' })
      })
      .then(() => {
        CartCoupon.refreshCart();
      })
      .catch(() => {
        CartCoupon.refreshCart();
      });
    }

    /* ── validation ─────────────────────────────────────────── */

    _validate(code, isExplicit = false) {
      this._setLoading(true);
      this._clearFeedback();
      const validateId = ++this._validateId;

      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ discount: code })
      })
      .then((r) => {
        if (!r.ok) throw new Error('Network response not ok');
        return fetch('/cart.js');
      })
      .then((r) => r.json())
      .then((cart) => {
        if (validateId !== this._validateId) return;
        this._setLoading(false);
        const appliedDiscounts = cart.cart_level_discount_applications || cart.discount_applications || [];
        let isApplied = false;
        let savedAmount = 0;

        appliedDiscounts.forEach((app) => {
          if (app.title && app.title.toUpperCase() === code.toUpperCase()) {
            isApplied = true;
            if (app.total_allocated_amount !== undefined) {
              savedAmount += parseFloat(app.total_allocated_amount);
            }
          }
        });

        if (!isApplied && cart.items) {
          cart.items.forEach(item => {
            if (item.line_level_discount_allocations) {
              item.line_level_discount_allocations.forEach(alloc => {
                if (alloc.discount_application && 
                    alloc.discount_application.title && 
                    alloc.discount_application.title.toUpperCase() === code.toUpperCase()) {
                  isApplied = true;
                  if (alloc.amount !== undefined) {
                    savedAmount += parseFloat(alloc.amount);
                  }
                }
              });
            }
          });
        }

        if (isApplied) {
          this._applyCode(code, savedAmount);
        } else {
          this._clearShopifySession();
          if (isExplicit) {
            this._setFeedback(this.dataset.invalidMsg, 'error');
          }
        }
      })
      .catch((err) => {
        if (validateId !== this._validateId) return;
        this._setLoading(false);
        if (isExplicit) {
          console.error("Discount validation error:", err);
          this._setFeedback(this.dataset.invalidMsg, 'error');
        }
      });
    }

    _applyCode(code, savedAmount = null) {
      this._code = code;
      sessionStorage.setItem('applied_coupon_code', code);
      this._showApplied(code, savedAmount);
      CartCoupon.stampBadges(code);
      this._clearFeedback();
      this.setAttribute('data-applied-code', code);
      CartCoupon.refreshCart();
    }

    /* ── Shopify session ────────────────────────────────────── */

    _clearShopifySession() {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' })
      }).catch(() => {});
    }

    /* ── UI state ───────────────────────────────────────────── */

    _showApplied(code, savedAmount = null) {
      this._inputWrap.style.display  = 'none';
      this._appliedWrap.style.display = 'flex';
      if (this._appliedText) {
        const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        if (savedAmount !== null && savedAmount > 0) {
          let formatted = `₹${(savedAmount / 100).toFixed(2)}`;
          if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
            formatted = Shopify.formatMoney(savedAmount, window.theme?.moneyFormat || '${{amount}}');
          }
          this._appliedText.innerHTML = `Code <strong>${escaped}</strong> applied (Saved ${formatted})`;
        } else if (!this._appliedText.innerHTML.trim() || this._appliedText.innerHTML.includes('at checkout')) {
          this._appliedText.innerHTML = `Code <strong>${escaped}</strong> applied`;
        }
      }
    }

    _showInput(skipFocus) {
      this._inputWrap.style.display   = '';
      this._appliedWrap.style.display  = 'none';
      this._input.value = '';
      if (!skipFocus) this._input.focus();
    }

    _setLoading(on) {
      this._input.disabled   = on;
      this._applyBtn.disabled = on;
      if (on) {
        this._applyBtn.dataset.label = this._applyBtn.textContent;
        this._applyBtn.innerHTML = '<span class="cd-coupon__spinner" aria-hidden="true"></span>';
      } else {
        this._applyBtn.textContent = this._applyBtn.dataset.label || 'APPLY';
      }
    }

    _setFeedback(msg, type) {
      this._feedback.textContent = msg || '';
      this._feedback.className   = `cd-coupon__feedback${type ? ` cd-coupon__feedback--${type}` : ''}`;
    }

    _clearFeedback() { this._setFeedback(''); }

    /* ── static: product card badges ───────────────────────── */

    static makeBadge(code) {
      const el = document.createElement('span');
      el.className = 'ci-coupon-badge';
      el.setAttribute('data-coupon-badge', '');

      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('class', 'ci-coupon-badge__icon');
      icon.setAttribute('width', '12');
      icon.setAttribute('height', '12');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('fill', 'none');
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML =
        '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"' +
        ' stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<circle cx="7" cy="7" r="1.5" fill="currentColor"/>';

      const text = document.createElement('span');
      text.className = 'ci-coupon-badge__text';
      text.textContent = code;

      const rm = document.createElement('span');
      rm.setAttribute('role', 'button');
      rm.setAttribute('tabindex', '0');
      rm.className = 'ci-coupon-badge__remove';
      rm.setAttribute('aria-label', `Remove coupon code ${code}`);
      rm.innerHTML =
        '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
          '<line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="3.5" stroke-linecap="round"/>' +
          '<line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="3.5" stroke-linecap="round"/>' +
        '</svg>';
      const doRemove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const couponEl = document.querySelector('cart-coupon');
        if (couponEl) couponEl._onRemove();
      };
      rm.addEventListener('click', doRemove);
      rm.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') doRemove(e);
      });

      el.appendChild(icon);
      el.appendChild(text);
      el.appendChild(rm);
      return el;
    }

    static _insertBadge(code, parent, anchorSelectors) {
      if (!parent || parent.querySelector('[data-coupon-badge]')) return;
      let anchor = null;
      for (const sel of anchorSelectors) {
        anchor = parent.querySelector(sel);
        if (anchor) break;
      }
      if (anchor) anchor.insertAdjacentElement('afterend', CartCoupon.makeBadge(code));
      else parent.appendChild(CartCoupon.makeBadge(code));
    }

    static stampBadges(code) {
      CartCoupon.removeBadges();
      // mini-cart
      document.querySelectorAll('.ci-body').forEach((body) => {
        CartCoupon._insertBadge(code, body, ['.ci-prices', '.ci-stock']);
      });
      // full cart page
      document.querySelectorAll('.cart-item__details').forEach((details) => {
        CartCoupon._insertBadge(code, details, ['.cart-item__price-wrapper']);
      });
    }

    static removeBadges() {
      document.querySelectorAll('[data-coupon-badge]').forEach((el) => el.remove());
    }

    /* ── static: checkout interceptor ──────────────────────── */

    static _attachCheckoutInterceptor() {
      if (CartCoupon._checkoutInterceptorAttached) return;
      CartCoupon._checkoutInterceptorAttached = true;

      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.bls-btn-checkout, .cd-footer__checkout-btn');
        if (!btn || btn.disabled) return;
        const couponEl = document.querySelector('cart-coupon');
        const code     = couponEl && couponEl.code;
        e.preventDefault();
        e.stopImmediatePropagation();
        window.location.href = code
          ? `/checkout?discount=${encodeURIComponent(code)}`
          : '/checkout';
      });
    }

    /* ── static: MutationObserver — re-stamp on cart re-renders */

    static _watchCartRerenders() {
      if (CartCoupon._observerAttached) return;
      CartCoupon._observerAttached = true;

      new MutationObserver((mutations) => {
        const couponEl = document.querySelector('cart-coupon');
        const code     = couponEl && couponEl.code;
        if (!code) return;

        mutations.forEach(({ addedNodes }) => {
          addedNodes.forEach((node) => {
            if (node.nodeType !== 1 || !node.querySelectorAll) return;
            node.querySelectorAll('.ci-body').forEach((body) => {
              CartCoupon._insertBadge(code, body, ['.ci-prices', '.ci-stock']);
            });
            node.querySelectorAll('.cart-item__details').forEach((details) => {
              CartCoupon._insertBadge(code, details, ['.cart-item__price-wrapper']);
            });
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }

    static refreshCart() {
      document.body.classList.add('start', 'loading');

      const hasCartPage = !!document.getElementById('main-cart-items');
      const hasMiniCart = !!document.getElementById('form-mini-cart');

      const sectionsToFetch = [];
      if (hasCartPage) {
        const cartPageId = document.getElementById('main-cart-items').dataset.id;
        sectionsToFetch.push(cartPageId);
      }
      if (hasMiniCart) {
        sectionsToFetch.push('form-mini-cart');
      }

      if (sectionsToFetch.length === 0) {
        document.body.classList.remove('start', 'loading');
        return;
      }

      const sectionsQuery = sectionsToFetch.join(',');
      fetch(`${window.location.pathname}?sections=${sectionsQuery}`)
        .then((r) => r.json())
        .then((sectionsData) => {
          // 1. Update cart page if present
          if (hasCartPage) {
            const cartPageId = document.getElementById('main-cart-items').dataset.id;
            const sectionHtml = sectionsData[cartPageId];
            if (sectionHtml) {
              const parser = new DOMParser();
              const html = parser.parseFromString(sectionHtml, 'text/html');

              const cartWrapper = document.querySelector('.cart-wrapper');
              const newCartWrapper = html.querySelector('.cart-wrapper');
              if (cartWrapper && newCartWrapper) {
                cartWrapper.innerHTML = newCartWrapper.innerHTML;
              } else {
                const jsContents = document.querySelector('.js-contents');
                const newJsContents = html.querySelector('.js-contents');
                if (jsContents && newJsContents) jsContents.innerHTML = newJsContents.innerHTML;
              }

              const totalsContent = document.querySelector('.cart__footer .totals');
              const newTotalsContent = html.querySelector('.cart__footer .totals');
              if (totalsContent && newTotalsContent) totalsContent.innerHTML = newTotalsContent.innerHTML;
            }
          }

          // 2. Update mini cart drawer if present
          if (hasMiniCart) {
            const sectionHtml = sectionsData['form-mini-cart'];
            if (sectionHtml) {
              const parser = new DOMParser();
              const html = parser.parseFromString(sectionHtml, 'text/html');

              const miniCartForm = document.getElementById('form-mini-cart');
              const newMiniCartForm = html.getElementById('form-mini-cart');
              if (miniCartForm && newMiniCartForm) {
                miniCartForm.innerHTML = newMiniCartForm.innerHTML;
                if (window.cartFbtForceCollapse) window.cartFbtForceCollapse();
              }

              const premiumBar = document.getElementById('premium-cart-bar-slot');
              const newPremiumBar = html.getElementById('premium-cart-bar-slot');
              if (premiumBar && newPremiumBar) {
                premiumBar.innerHTML = newPremiumBar.innerHTML;
              }

              const freeShip = document.querySelector('free-ship-progress-bar');
              const newFreeShip = html.querySelector('free-ship-progress-bar');
              if (freeShip && newFreeShip) {
                freeShip.outerHTML = newFreeShip.outerHTML;
              }
            }
          }

          // 3. Update totals / pricing summary globally
          const priceDetails = document.querySelector('[data-price-details]');
          if (priceDetails) {
            let newPriceDetails = null;
            for (const key in sectionsData) {
              const parser = new DOMParser();
              const html = parser.parseFromString(sectionsData[key], 'text/html');
              newPriceDetails = html.querySelector('[data-price-details]');
              if (newPriceDetails) break;
            }
            if (newPriceDetails) {
              const wasExpanded = priceDetails.classList.contains('cd-footer__price-card--user-expanded');
              priceDetails.innerHTML = newPriceDetails.innerHTML;
              if (wasExpanded || newPriceDetails.classList.contains('cd-footer__price-card--user-expanded')) {
                priceDetails.classList.add('cd-footer__price-card--user-expanded');
              }
            }
          }

          const cartSummary = document.querySelector('[data-cart-summary]');
          if (cartSummary) {
            let newCartSummary = null;
            for (const key in sectionsData) {
              const parser = new DOMParser();
              const html = parser.parseFromString(sectionsData[key], 'text/html');
              newCartSummary = html.querySelector('[data-cart-summary]');
              if (newCartSummary) break;
            }
            if (newCartSummary) cartSummary.innerHTML = newCartSummary.innerHTML;
          }

          const discountsList = document.querySelector('[data-discounts-list]');
          if (discountsList) {
            let newDiscountsList = null;
            for (const key in sectionsData) {
              const parser = new DOMParser();
              const html = parser.parseFromString(sectionsData[key], 'text/html');
              newDiscountsList = html.querySelector('[data-discounts-list]');
              if (newDiscountsList) break;
            }
            if (newDiscountsList) discountsList.innerHTML = newDiscountsList.innerHTML;
          }

          // 4. Update cart counts
          fetch('/cart.json')
            .then((res) => res.json())
            .then((cart) => {
              if (cart.item_count !== undefined) {
                document.querySelectorAll(".cart-count").forEach(el => {
                  el.innerHTML = el.classList.contains('cart-count-drawer')
                    ? `(${cart.item_count})`
                    : cart.item_count;
                });
              }
            }).catch(() => {});

          // 5. Re-stamp custom badges
          const couponEl = document.querySelector('cart-coupon');
          const code = couponEl && couponEl.code;
          if (code) {
            CartCoupon.stampBadges(code);
          } else {
            CartCoupon.removeBadges();
          }
        })
        .catch((e) => console.error("Error refreshing cart:", e))
        .finally(() => {
          document.body.classList.add('finish');
          setTimeout(() => {
            document.body.classList.remove('start', 'loading', 'finish');
          }, 500);
          if (typeof BlsLazyloadImg !== 'undefined' && BlsLazyloadImg.init) {
            BlsLazyloadImg.init();
          }
        });
    }
  });
}
